import { createContext, useContext } from "react";

/**
 * Session-lifetime, messageId-keyed store for optimistic Context Enrichment
 * state (dismissed / refined) that must survive `ContextEnrichmentCard`
 * unmount/remount.
 *
 * Why this exists: assistant-ui caches thread runtimes (and their message
 * content parts) in memory keyed by threadId. `ThreadHistoryAdapter.load()`
 * only runs once per thread per page-load, so the card's local `useState`
 * optimism is lost when the user navigates to another thread and returns;
 * the cached `data.dismissed` / `data.refined` is still the server value
 * from the initial load. Anchoring the optimistic state above the card's
 * lifecycle (alongside `RefineContext` inside `RuntimeProvider`) keeps
 * Dismiss and Refine from "unstuck"-ing on thread navigation within the
 * same page session. On full page refresh, `history.load()` re-fetches
 * server truth and the store resets, so the store only covers the
 * in-session gap.
 *
 * Keyed by `messageId: number` alone, since assistant message ids are
 * unique across the tenant and never reused within a session, so the
 * store does not need to be scoped by conversation.
 */
export interface EnrichmentStateValue {
    isDismissed: (messageId: number) => boolean;
    isRefined: (messageId: number) => boolean;
    markDismissed: (messageId: number) => void;
    unmarkDismissed: (messageId: number) => void;
    markRefined: (messageId: number) => void;
    unmarkRefined: (messageId: number) => void;
}

export const EnrichmentStateContext =
    createContext<EnrichmentStateValue | null>(null);

export function useEnrichmentState(): EnrichmentStateValue {
    const ctx = useContext(EnrichmentStateContext);
    if (ctx === null) {
        throw new Error(
            "useEnrichmentState must be used inside a <RuntimeProvider>"
        );
    }
    return ctx;
}
